import React, { useEffect, useRef, useState, useMemo } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, Grid, TextField, MenuItem, CircularProgress, Divider,
    Snackbar, Alert, useMediaQuery
} from "@mui/material";
import {
    NavigateNext, ArrowBack, Save, Clear, Badge, Event
} from '@mui/icons-material';
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6"
};

const AddBatchScreen = () => {
    const navigation = useNavigate();
    const formikRef = useRef();
    const isNonMobile = useMediaQuery("(min-width:700px)");

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Generate Year List (Standardized: Current Year + Next Year)
    const yearList = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const list = [];
        for (let i = 0; i < 5; i++) {
            const yr = (currentYear + 1 - i).toString();
            list.push({ id: yr, title: yr });
        }
        return list;
    }, []);

    useEffect(() => {
        localStorage.setItem("current_tab", "Batch");
    }, []);

    const handleCreate = async (values, { resetForm }) => {
        setLoadingSubmit(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/batch`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(values)
            });
            const resJson = await response.json();

            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Batch created successfully!", severity: "success" });
                resetForm();
                setTimeout(() => navigation("/Portal/Batch"), 1500);
            } else {
                setSnackbar({ open: true, message: resJson.message || "Failed to create batch", severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Connection Error. Please try again.", severity: "error" });
        } finally {
            setLoadingSubmit(false);
        }
    };

    const initialValues = {
        batchName: "",
        batchYear: "",
        status: "A"
    };

    const validationSchema = yup.object({
        batchName: yup.string().required("Batch Name is required"),
        batchYear: yup.string().required("Batch Year is required"),
    });

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                {/* Navigation & Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Batch")} sx={{ cursor: 'pointer' }}>Batch</Link>
                            <Typography color="text.primary">Create Batch</Typography>
                        </Breadcrumbs>
                        <Header title="Add New Batch" subtitle="Define a new academic cycle or student group" />
                    </Box>
                    <Button startIcon={<ArrowBack />} onClick={() => navigation("/Portal/Batch")} color="inherit">Back</Button>
                </Stack>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleCreate}
                >
                    {({ values, errors, touched, handleBlur, handleChange }) => (
                        <Form>
                            <Grid container spacing={3}>

                                {/* Main Form Area */}
                                <Grid item xs={12} md={8}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: '1px solid #F0E6CC' }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                            <Badge sx={{ color: colors.textMain }} />
                                            <Typography variant="h6" fontWeight="bold">Batch Details</Typography>
                                        </Stack>

                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Batch Name <span style={{ color: 'red' }}>*</span></Typography>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="batchName"
                                                    placeholder="e.g. SPM 2025 / Tahun 1"
                                                    value={values.batchName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.batchName && !!errors.batchName}
                                                    helperText={touched.batchName && errors.batchName}
                                                    sx={{ bgcolor: 'white' }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Academic Year <span style={{ color: 'red' }}>*</span></Typography>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    name="batchYear"
                                                    value={values.batchYear}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.batchYear && !!errors.batchYear}
                                                    helperText={touched.batchYear && errors.batchYear}
                                                    sx={{ bgcolor: 'white' }}
                                                >
                                                    <MenuItem value="" disabled>Select Year</MenuItem>
                                                    {yearList.map((val) => (
                                                        <MenuItem key={val.id} value={val.id}>{val.title}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Status</Typography>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    name="status"
                                                    value={values.status}
                                                    onChange={handleChange}
                                                    sx={{ bgcolor: 'white' }}
                                                >
                                                    <MenuItem value="A">Active</MenuItem>
                                                    <MenuItem value="I">Inactive</MenuItem>
                                                </TextField>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Helpful Sidebar Info */}
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #F0E6CC', borderRadius: 3, bgcolor: '#FBFBFB', height: '100%' }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                            <Event sx={{ color: colors.accent }} />
                                            <Typography variant="h6" fontWeight="bold">Usage Tip</Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                                            Batches are used to group students by their academic year. Once created, you can assign this batch to new student registrations and filter reports accordingly.
                                        </Typography>
                                    </Paper>
                                </Grid>

                                {/* Submission Actions */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button variant="outlined" onClick={() => navigation("/Portal/Batch")} startIcon={<Clear />} sx={{ borderRadius: 2 }}>
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loadingSubmit}
                                            startIcon={loadingSubmit ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                            sx={{
                                                bgcolor: colors.accent,
                                                color: 'black',
                                                px: 6,
                                                borderRadius: 3,
                                                fontWeight: 'bold',
                                                '&:hover': { bgcolor: colors.accentDark }
                                            }}
                                        >
                                            {loadingSubmit ? "Saving..." : "Create Batch"}
                                        </Button>
                                    </Stack>
                                </Grid>

                            </Grid>
                        </Form>
                    )}
                </Formik>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </Box>
        </Box>
    );
};

export default AddBatchScreen;